'use client';

import { useEffect, useState } from 'react';
import type { Listing } from '@repo/api';
import type { ChatTourType } from '@repo/utils';
import { useStore } from '@/providers/StoreProvider';
import { useRouter } from 'next/navigation';
import { useIsMobile } from '@/hooks/useDeviceType';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useChatSession } from '@/hooks/useChatSession';
import { getListingUrgency } from '@/lib/listings/urgency';
import { disconnectWebSocket } from '@/lib/chat/chat-actions';
import { ChatInput } from './ChatInput';
import { ChatMessageList } from './ChatMessageList';
import { Modal } from '@/components/ui/Modal';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { ResponsiveLink } from '@/components/shared/ResponsiveLink';
import { IconChevronLeft, IconChevronRight, IconCross } from '@repo/svgs';

// ─── Prop shapes ──────────────────────────────────────────────────────────────

type PageProps = {
  variant: 'page';
  chatId: string;
  unitSlug?: string;
  initialUnitData?: {
    fullAddress?: string;
    address?: string;
    slug?: string;
    numBedrooms?: number;
    numBathrooms?: number;
    addressSlug?: string;
    [key: string]: any;
  };
};

type OverlayProps = {
  variant: 'overlay';
  open: boolean;
  onClose: () => void;
  listing: Listing;
  /** The call to action that opened the chat, sent when the chat is created. */
  tourType?: ChatTourType;
};

export type ChatDetailProps = PageProps | OverlayProps;

// ─── Page variant (full-page /messages layout) ────────────────────────────────

function ChatDetailPage({ chatId, unitSlug: propUnitSlug, initialUnitData }: PageProps) {
  const router = useRouter();
  const isMobile = useIsMobile();
  const analytics = useAnalytics();
  const chatDetailData = useStore(s => s.chat.chatDetailData);

  const unit = (() => {
    if (chatDetailData?.chat?.id === chatId && chatDetailData?.chat?.unit)
      return chatDetailData.chat.unit as any;
    return initialUnitData || (chatDetailData?.chat?.unit as any) || null;
  })();

  const unitSlug = propUnitSlug || unit?.slug;
  const fullAddress = unit?.fullAddress || unit?.address || '';
  const numBedrooms = unit?.numBedrooms ?? 0;
  const numBathrooms = unit?.numBathrooms ?? 0;
  const bedroomLabel =
    numBedrooms === 0 ? 'Studio' : numBedrooms === 1 ? '1 bed' : `${numBedrooms} beds`;
  const bathroomLabel = numBathrooms === 1 ? '1 bath' : `${numBathrooms} baths`;
  const streetAddress = fullAddress?.split(',').map((p: string) => p.trim())[0] || '';
  const addressSlug = unit?.addressSlug || '';

  const session = useChatSession({ chatId, unitSlug, enabled: true });

  const [isInputFocused, setIsInputFocused] = useState(false);

  return (
    <div className="flex lg:border border-secondary rounded-3xl w-full h-full flex-col bg-primary overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 flex justify-center md:justify-start items-center gap-3 border-b border-tertiary px-6 py-4 bg-primary">
        {isMobile && (
          <button
            onClick={() => {
              analytics.track('closed_message');
              router.replace('/messages', { scroll: false });
            }}
            className="flex-shrink-0 fixed left-7 text-secondary hover:text-primary transition-colors"
            aria-label="Back to messages"
          >
            <IconChevronLeft className="w-3 h-3" />
          </button>
        )}
        <div>
          {unitSlug ? (
            <ResponsiveLink
              href={`/unit/nyc/${unitSlug}/${addressSlug}`}
              className="text-body-1-high text-primary font-semibold hover:underline flex items-center gap-1"
              onClick={() => analytics.track('chat_header_address_click')}
            >
              {streetAddress}
              <IconChevronRight className="w-3 h-3 ml-1" />
            </ResponsiveLink>
          ) : (
            <h2 className="text-body-1-high text-primary font-semibold">{streetAddress}</h2>
          )}
          <p className="text-body-2 text-secondary text-center md:text-left">
            {bedroomLabel}, {bathroomLabel}
          </p>
        </div>
      </div>

      {/* Messages */}
      <ChatMessageList
        key={chatId}
        messages={session.sortedMessages}
        isInitializing={session.isInitializing}
        isPolling={session.isPolling}
        fetchPaginatedChat={session.fetchPaginatedChat}
        totalMessageCount={session.totalMessageCount}
        shouldAutoScroll={session.shouldAutoScroll}
        setShouldAutoScroll={session.setShouldAutoScroll}
        userSentMessageRef={session.userSentMessageRef}
        onLoadMore={session.loadNextPage}
        onSlotSelect={(value, msg) => void session.handleSlotSelect(value, msg)}
        onRetry={msg => void session.handleRetryMessage(msg)}
        isOverlay
      />

      {/* Input */}
      <div className="flex-shrink-0 px-6 py-4 bg-primary">
        <ChatInput
          value={session.inputValue}
          onChange={session.setInputValue}
          onSend={() => void session.handleSendMessage()}
          placeholder="Your message"
          disabled={
            !chatId ||
            session.wsStatus !== 'connected' ||
            session.isPolling ||
            session.wsConnectedChatId !== chatId
          }
          inputDisabled={
            !session.chatToken || !session.chatDetailData || session.wsStatus === 'connecting'
          }
          isFocused={isInputFocused}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
        />
      </div>
    </div>
  );
}

// ─── Overlay variant (Modal / BottomSheet from listing page) ──────────────────

function ChatDetailOverlay({ open, onClose, listing, tourType }: OverlayProps) {
  const isMobile = useIsMobile();
  const [isBelowMd, setIsBelowMd] = useState(false);

  useEffect(() => {
    const check = () => setIsBelowMd(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const shouldUseBottomSheet = isMobile || isBelowMd;

  const session = useChatSession({
    unitSlug: listing.slug,
    createIfMissing: true,
    ...(tourType ? { tourType } : {}),
    enabled: open,
    listingContext: {
      isFutureListing: listing.activeListing?.isFutureListing ?? undefined,
      isVerified: listing.isVerified ?? undefined,
      urgency: getListingUrgency(listing) ?? undefined,
    },
  });

  // iOS Safari: close overlay when app is backgrounded
  useEffect(() => {
    if (!open || !shouldUseBottomSheet) return;
    const handle = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') return;
      try {
        (document.activeElement as HTMLElement | null)?.blur?.();
      } catch {}
      disconnectWebSocket();
      onClose();
    };
    document.addEventListener('visibilitychange', handle);
    window.addEventListener('pagehide', handle);
    return () => {
      document.removeEventListener('visibilitychange', handle);
      window.removeEventListener('pagehide', handle);
    };
  }, [open, onClose, shouldUseBottomSheet]);

  const numBedrooms = listing.numBedrooms ?? 0;
  const numBathrooms = listing.numBathrooms ?? 0;
  const bedroomLabel =
    numBedrooms === 0 ? 'Studio' : numBedrooms === 1 ? '1 bed' : `${numBedrooms} beds`;
  const bathroomLabel = numBathrooms === 1 ? '1 bath' : `${numBathrooms} baths`;
  const headerTitle = listing.fullAddress?.split(',')[0] || listing.addressSlug || 'Unit';
  const headerSubtitle = `${bedroomLabel}, ${bathroomLabel}`;

  const closeButton = (
    <button
      onClick={onClose}
      className="text-secondary hover:text-primary transition-colors"
      aria-label="Close chat"
    >
      <IconCross className="w-4 h-4 icon-secondary" />
    </button>
  );

  const messageList = (
    <ChatMessageList
      messages={session.sortedMessages}
      isInitializing={session.isInitializing}
      isPolling={session.isPolling}
      fetchPaginatedChat={session.fetchPaginatedChat}
      totalMessageCount={session.totalMessageCount}
      shouldAutoScroll={session.shouldAutoScroll}
      setShouldAutoScroll={session.setShouldAutoScroll}
      userSentMessageRef={session.userSentMessageRef}
      onLoadMore={session.loadNextPage}
      onSlotSelect={(value, msg) => void session.handleSlotSelect(value, msg)}
      onRetry={msg => void session.handleRetryMessage(msg)}
    />
  );

  const chatInput = (
    <ChatInput
      value={session.inputValue}
      onChange={session.setInputValue}
      onSend={() => void session.handleSendMessage()}
      placeholder="Your message"
      disabled={!session.resolvedChatId || session.wsStatus !== 'connected' || session.isPolling}
      inputDisabled={session.isInitializing}
      onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
    />
  );

  if (shouldUseBottomSheet) {
    return (
      <BottomSheet
        open={open}
        onOpenChange={isOpen => !isOpen && onClose()}
        label={headerTitle}
        subtitle={headerSubtitle}
        onClose={onClose}
        dismissible={true}
        fullHeight={true}
        trailing={closeButton}
        bodyClassName="!p-0 flex flex-col flex-1 min-h-0 overflow-hidden"
        footer={<div className="px-6 py-4">{chatInput}</div>}
        footerClassName="!border-none"
      >
        {messageList}
      </BottomSheet>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      onOpenChange={isOpen => !isOpen && onClose()}
      dismissible={true}
      label={headerTitle}
      trailing={closeButton}
      maxWidth="md"
      contentClassName="h-[calc(100vh-2rem)]"
      bodyClassName="!p-0 flex flex-col flex-1 min-h-0 overflow-hidden"
      overlayClassName="md:items-end md:justify-end md:pr-4 md:pb-4"
    >
      {messageList}
      <div className="flex-shrink-0 px-6 py-4">{chatInput}</div>
    </Modal>
  );
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function ChatDetail(props: ChatDetailProps) {
  if (props.variant === 'overlay') return <ChatDetailOverlay {...props} />;
  return <ChatDetailPage {...props} />;
}
